import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';

import { ITempControllSetting, TEMP_SETTINGS, TempSetting } from '../../../globals/interfaces';
import BackButton from '../../../components/custom/BackButton';
import ActionIcon from '../../../components/custom/ActionIcon';
import { createdUpdatedInfo, notify } from '../../../utils';
import { Nav } from 'reactstrap';
import CustomNavItem from '../../../components/custom/CustomNavItem';
import { TabContent } from 'reactstrap';
import CustomTabPane from '../../../components/custom/CustomTabPane';
import { ButtonGroup } from 'reactstrap';
import { Button } from 'reactstrap';
import { UpdateTempSetting } from './useData';
import Log from './Log';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    item: ITempControllSetting | undefined;
    unit: string;
    updateTempSetting: UpdateTempSetting;
}

export type Tab = 'SETTING' | 'LOG'

export default function ModalManage ({
    modal,
    setModal,
    item,
    unit,
    updateTempSetting
}: Props) {

    const [activeTab, setActiveTab] = useState<Tab>('SETTING');
    const [setting, setSetting] = useState<TempSetting>('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (item && item.setting) {
            setSetting(item.setting);
        } else {
            setSetting('');
        }
    }, [item]);

    const toggleTab = (tab: Tab) => setActiveTab(tab);
    const toggle = () => setModal(!modal);
    const handleUpdate = async (setting: TempSetting) => {
        setLoading(true);
        await updateTempSetting(setting);
        toggle();
        setLoading(false);
        notify('Temp setting updated');
    }

    const renderTemp = (temp: TempSetting) => temp === 'OFF' ?
        temp : `${temp}°C`;


    if (!item) return null;

    return (
        <Modal isOpen={modal} toggle={toggle} size={'lg'}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>{item.location}</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <Nav tabs className={'separator-tabs'}>
                    <CustomNavItem<Tab>
                        tabName="Setting"
                        tabId="SETTING"
                        activeTabId={activeTab}
                        toggleTab={toggleTab}
                    />
                    <CustomNavItem<Tab>
                        tabName="Log"
                        tabId="LOG"
                        activeTabId={activeTab}
                        toggleTab={toggleTab}
                    />
                </Nav>
                <TabContent activeTab={activeTab} className={'mt-2'}>
                    <CustomTabPane<Tab> tabId={'SETTING'}>
                        <Label>Current Temperature: {item.tempVal}&deg;C</Label>
                        <SettingsContainer>
                            <Label>Setting</Label>
                            <ButtonGroup>
                                {
                                    TEMP_SETTINGS.map((item, i) => (
                                        <Button 
                                            key={i}
                                            onClick={() => setSetting(item)}
                                            active={setting === item}
                                        >
                                            {renderTemp(item)}
                                        </Button>
                                    ))
                                }
                            </ButtonGroup>
                        </SettingsContainer>
                    </CustomTabPane>
                    <CustomTabPane<Tab> tabId={'LOG'}>
                        <Log 
                            unit={unit}
                            item={item}
                            activeTab={activeTab}
                        />
                    </CustomTabPane>
                </TabContent>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label>
                        {createdUpdatedInfo({item})}
                    </Label>
                    <FooterButtonsContainer>
                        <ActionIcon 
                            type={'save'} 
                            onClick={() => handleUpdate(setting)} 
                            disabled={!setting}
                            loading={loading}
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{update?: boolean}>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;

const SettingsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;